import type { LucideIcon } from "lucide-react";
import type { ComponentProps } from "react";
import type { Components } from "react-markdown";

import { AssistantResponseMarkdownView } from "@product/components/app/runs/assistant-response-markdown-view";
import { MENTION_ENTITY_STYLES } from "@product/components/app/sessions/entity-mention-input";
import { EntityHoverTargetView } from "@product/components/content/entity-card";
import { normalizeMarkdownContent } from "@product/components/content/markdown-normalize";
import { WorkspaceLinkView } from "@product/components/content/workspace-link-view";
import { cn } from "@v1/ui/utils";

import { SHOT_MARKDOWN_COMPONENTS } from "./markdown";

/**
 * Someone or something a post mentions, as the feed resolves it from the
 * post's mention ids: a teammate by name, or a channel, data set or view
 * with the channel it lives in.
 */
export type ShotMention =
  | { type: "teammate"; id: string; label: string }
  | { type: "channel"; id: string; label: string }
  | { type: "dataSet"; id: string; label: string; channelId: string; channelName: string }
  | { type: "view"; id: string; label: string; channelId: string; channelName: string; icon: LucideIcon };

/** The link a mention becomes in the markdown, as EntityMentionMarkdown writes it. */
const MENTION_PREFIX = "#mlx-entity-mention-";

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
const escapeLinkLabel = (value: string) => value.replace(/([\\[\]])/gu, "\\$1");

/**
 * The post's text with each "@Name" turned into a link the renderer draws
 * as a mention, the longest names first so one name inside another keeps
 * its own. A mention the text never makes is a fixture's mistake.
 */
export function withMentionLinks(body: string, mentions: readonly ShotMention[]): string {
  if (mentions.length === 0) return body;
  const tokens = mentions.map((mention, index) => ({ token: `@${mention.label}`, index })).sort((left, right) => right.token.length - left.token.length);
  for (const { token } of tokens) {
    if (!body.includes(token)) throw new Error(`The post mentions ${token} but its text does not: "${body}"`);
  }
  const matcher = new RegExp(`(${tokens.map(({ token }) => escapeRegExp(token)).join("|")})`, "gu");
  return body
    .split(matcher)
    .map((part) => {
      const mention = tokens.find(({ token }) => token === part);
      return mention ? `[${escapeLinkLabel(part)}](${MENTION_PREFIX}${mention.index})` : part;
    })
    .join("");
}

/** A mention as the feed draws it at rest: a teammate in the mention's colour, a thing as the pill a link to it shows. */
function Mention({ mention, children }: { mention: ShotMention; children: ComponentProps<"a">["children"] }) {
  if (mention.type === "teammate") {
    return (
      <EntityHoverTargetView label={`${mention.label}: details`} className={cn("rounded px-0.5 font-medium", MENTION_ENTITY_STYLES.teammate.inline)}>
        {children}
      </EntityHoverTargetView>
    );
  }
  if (mention.type === "channel") {
    return <WorkspaceLinkView href={`/workspace/channels/${encodeURIComponent(mention.id)}`} kind="channel" title={mention.label} detail={null} />;
  }
  const channelHref = `/workspace/channels/${encodeURIComponent(mention.channelId)}`;
  if (mention.type === "dataSet") {
    return <WorkspaceLinkView href={`${channelHref}?dataSet=${encodeURIComponent(mention.id)}`} kind="dataSet" title={mention.label} detail={`#${mention.channelName}`} />;
  }
  return (
    <WorkspaceLinkView
      href={`${channelHref}?view=${encodeURIComponent(`saved:${mention.id}`)}`}
      kind="view"
      title={mention.label}
      detail={`#${mention.channelName}`}
      icon={mention.icon}
    />
  );
}

/**
 * A channel post's text as the feed renders it (EntityMentionMarkdown): the
 * product's markdown, its mentions drawn as the product draws them, and any
 * other link a record's chip. The application looks a mention's names up and
 * opens its card on hover; a shot has the names already and stays at rest.
 */
export function ShotMentionMarkdown({ body, mentions = [] }: { body: string; mentions?: readonly ShotMention[] }) {
  const RecordLink = SHOT_MARKDOWN_COMPONENTS.a as (props: ComponentProps<"a"> & { node?: unknown }) => React.ReactNode;
  const components: Partial<Components> = {
    ...SHOT_MARKDOWN_COMPONENTS,
    a: ({ node, href, children }) => {
      if (!href?.startsWith(MENTION_PREFIX)) return RecordLink({ node, href, children });
      const mention = mentions[Number(href.slice(MENTION_PREFIX.length))];
      return mention ? <Mention mention={mention}>{children}</Mention> : <>{children}</>;
    },
  };
  return (
    <AssistantResponseMarkdownView className="max-w-none" components={components}>
      {normalizeMarkdownContent(withMentionLinks(body, mentions), { streaming: false })}
    </AssistantResponseMarkdownView>
  );
}
